/**
 * THE ONE MEDIA SESSION THIS DOCUMENT HAS OPEN FOR A CALL, by call id.
 *
 * WHY A REGISTRY AND NOT JUST REACT. Until now the only thing that could stop a
 * call's microphone was React unmounting `<CallRoom>`, whose `useCallSession`
 * cleanup calls `client.disconnect()`. That works — it is verified in
 * test/callEnd.test.mjs — but it is IMPLICIT: the mic is released as a side
 * effect of a render decision three components away, and every path that ends a
 * call has to happen to produce that render. The 21 Aug call is what happens
 * when one of them does not. The owner sat in a call the other side had left,
 * publishing audio into an empty SFU room, for as long as he chose to sit there.
 *
 * So the exit is named, addressable and callable from anywhere that learns the
 * call is over — the realtime event, the server reconciliation, the user's own
 * hangup — instead of being an emergent property of the tree. It is idempotent,
 * so all three may fire, and it is KEYED BY CALL ID, which is the part that
 * matters: a transfer and a merge both end one call while this document is
 * already holding the media for a DIFFERENT one, and an unkeyed "stop the
 * media" would hang up the call the user just moved into.
 */
import type { MediaClient, MediaConnectionState } from '@/features/meeting/media/contract';

interface Registration {
  readonly callId: string;
  readonly client: MediaClient;
  /** Stops watching the snapshot. Called before the deliberate disconnect. */
  stopWatching(): void;
}

/**
 * States that mean "this session is not in the room any more".
 *
 * `reconnecting` is deliberately absent: the provider takes its seat back after
 * a drop nobody asked for (a frozen background tab, an ICE restart), and a
 * seat being retaken is not a call ending. See REJOIN_BACKOFF_MS.
 */
const OFF_THE_ROOM: ReadonlySet<MediaConnectionState> = new Set<MediaConnectionState>([
  'disconnected',
  'failed',
]);

let live: Registration | null = null;
const dropped = new Set<(callId: string) => void>();

/**
 * Hand the call's media session over for the life of the room.
 *
 * Returns the deregistration, which does NOT disconnect: an unmount already
 * disconnects through `useCallSession`, and disconnecting twice would race a
 * brand-new session that has taken this one's place (answer, then "Call again"
 * on the ended screen, in the same document).
 */
export function registerCallMedia(callId: string, client: MediaClient): () => void {
  if (callId === '') return () => undefined;
  let previous: MediaConnectionState = client.getSnapshot().state;
  const stopWatching = client.subscribe(() => {
    const next = client.getSnapshot().state;
    if (next === previous) return;
    const wasOn = !OFF_THE_ROOM.has(previous);
    previous = next;
    /* Only the TRANSITION out of the room is worth telling anyone about, and
       only from a session that was in it. A session that never connected is a
       failure the UI is already showing. */
    if (wasOn && OFF_THE_ROOM.has(next)) dropped.forEach((listener) => listener(callId));
  });
  const registration: Registration = { callId, client, stopWatching };
  live = registration;
  return () => {
    stopWatching();
    if (live === registration) live = null;
  };
}

/**
 * LEAVE THE ROOM AND GIVE THE DEVICES BACK. The exit.
 *
 * `MediaClient.disconnect()` is what actually does it — it stops the rejoin
 * loop, disconnects the LiveKit room and calls `localMedia.stop()`, which calls
 * `track.stop()` on the real capture tracks, which is what turns the light off.
 *
 * Returns true when there was something to release, so a caller can tell the
 * difference between "the mic is now off" and "there was never a session here".
 */
export async function releaseCallMedia(callId: string): Promise<boolean> {
  const registration = live;
  if (registration === null || registration.callId !== callId) return false;
  live = null;
  /* Before the disconnect, not after: the disconnect drives the snapshot to
     `disconnected`, and a session we deliberately closed must not be announced
     as a session that dropped. */
  registration.stopWatching();
  await registration.client.disconnect().catch(() => undefined);
  return true;
}

/**
 * THE SFU SAYS THIS DOCUMENT IS NO LONGER IN THE ROOM.
 *
 * Not an ending on its own and never treated as one — a frozen tab produces it
 * too, and a call is not over because Chrome throttled a timer. It is a REASON
 * TO ASK the server, which is the only thing that may answer. `useCallLifecycle`
 * uses it to reconcile at once instead of waiting out the poll interval, so a
 * client the server has evicted (`deleteRoom` on hangup) finds out in one round
 * trip rather than in thirty seconds.
 */
export function onCallMediaDropped(listener: (callId: string) => void): () => void {
  dropped.add(listener);
  return () => {
    dropped.delete(listener);
  };
}

/** The live session's state, for tests and diagnostics. `null` when there is none. */
export function callMediaState(callId: string): MediaConnectionState | null {
  return live !== null && live.callId === callId ? live.client.getSnapshot().state : null;
}
